import { FC, type JSX } from 'react';
import { WithChildren } from '@/type-utils/component';
import { isNullOrEmpty } from '@/utils/null-utils';
import { Button, ButtonProps } from '../Button';
import { Link, LinkProps } from '../Link';
import { Icon, IconTypes, IIconProps } from '../Icon';

import S from './styles.base.module.scss';

/**
 * TooltipTitle component to display the title of the tooltip.
 */
export const TooltipTitle: FC<{ id?: string; title?: string }> = ({
  id,
  title
}) => {
  if (isNullOrEmpty(title)) return null;
  return (
    <div id={id ? `${id}_title` : undefined} className={S.title}>
      {title}
    </div>
  );
};

/**
 * TooltipBody component to display the body content of the tooltip.
 */
export const TooltipBody: FC<{ body?: string | JSX.Element }> = ({ body }) => {
  if (!body) return null;
  return <div className={S.body}>{body}</div>;
};

/**
 * TooltipIcon component to display an icon within the tooltip.
 */
export const TooltipIcon: FC<{
  icon?: IconTypes | 'none';
  iconProps?: IIconProps;
}> = ({ icon, iconProps }) => {
  if (!icon || icon === 'none') return null;
  return (
    <div className={S.iconContainer}>
      <Icon icon={icon} className={S.icon} {...iconProps} />
    </div>
  );
};

/**
 * TooltipFooter component to display action buttons and links within the tooltip.
 */
export const TooltipFooter: FC<{
  buttons?: Array<WithChildren<ButtonProps>>;
  links?: Array<WithChildren<LinkProps>>;
}> = ({ buttons, links }) => {
  if (!buttons && !links) return null;
  return (
    <div className={S.footer}>
      {buttons ? (
        <div className={S.buttons}>
          {buttons.map((b, idx) => (
            <Button key={`button_${idx}`} {...b} />
          ))}
        </div>
      ) : null}
      {links ? (
        <div className={S.links}>
          {links.map((l, idx) => (
            <Link key={`link_${idx}`} {...l} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
